(() => {
  if (window.AristaAttribution) return;

  const STORAGE_KEY = 'arista-attribution-v1';
  const SYNC_KEY = 'arista-attribution-cart-sync-v1';
  const ATTRIBUTION_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
  const MAX_VALUE_LENGTH = 500;
  const checkoutBypass = new WeakSet();
  let pendingSync = null;

  const readStored = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
      return stored && typeof stored === 'object' ? stored : {};
    } catch (_error) {
      return {};
    }
  };

  const writeStored = (values) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch (_error) {
      // The URL still carries attribution when session storage is unavailable.
    }
  };

  const captureAttribution = () => {
    const values = readStored();
    const query = new URLSearchParams(window.location.search);
    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = query.get(field);
      if (value) values[field] = value.slice(0, MAX_VALUE_LENGTH);
    });
    writeStored(values);
    return values;
  };

  const attributionValues = () => {
    const stored = readStored();
    return ATTRIBUTION_FIELDS.reduce((values, field) => {
      const value = stored[field];
      if (typeof value === 'string' && value.length > 0) values[field] = value.slice(0, MAX_VALUE_LENGTH);
      return values;
    }, {});
  };

  const withAttribution = (value) => {
    if (!value) return value;
    let url;
    try {
      url = new URL(value, window.location.href);
    } catch (_error) {
      return value;
    }
    if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) return value;

    const values = attributionValues();
    Object.entries(values).forEach(([field, fieldValue]) => {
      if (!url.searchParams.has(field)) url.searchParams.set(field, fieldValue);
    });
    return url.toString();
  };

  const syncCartAttributes = ({ force = false } = {}) => {
    const attributes = attributionValues();
    if (Object.keys(attributes).length === 0) return Promise.resolve(false);

    const signature = JSON.stringify(attributes);
    try {
      if (!force && window.sessionStorage.getItem(SYNC_KEY) === signature) return Promise.resolve(true);
    } catch (_error) {
      // A later checkout interaction will retry when storage is unavailable.
    }
    if (pendingSync) return pendingSync;

    pendingSync = fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ attributes }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Attribution cart sync failed.');
        try {
          window.sessionStorage.setItem(SYNC_KEY, signature);
        } catch (_error) {
          // Successful cart attributes remain the durable checkout bridge.
        }
        return true;
      })
      .catch(() => false)
      .finally(() => {
        pendingSync = null;
      });

    return pendingSync;
  };

  const waitForCheckoutSync = () =>
    Promise.race([
      syncCartAttributes({ force: true }),
      new Promise((resolve) => window.setTimeout(() => resolve(false), 1200)),
    ]);

  const isCheckoutControl = (element) =>
    Boolean(
      element?.matches?.(
        '#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
      )
    );

  const continueCheckoutClick = (control) => {
    const form = control.form || control.closest?.('form');
    checkoutBypass.add(control);
    if (form) checkoutBypass.add(form);
    control.click();
    window.setTimeout(() => {
      checkoutBypass.delete(control);
      if (form) checkoutBypass.delete(form);
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a[href]');
      if (link) {
        const decoratedHref = withAttribution(link.href);
        if (decoratedHref && decoratedHref !== link.href) link.href = decoratedHref;
      }

      const control = event.target.closest?.(
        '#CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
      );
      if (!isCheckoutControl(control) || checkoutBypass.has(control)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      waitForCheckoutSync().finally(() => continueCheckoutClick(control));
    },
    true
  );

  // Dynamic-payment buttons must keep their original trusted click so that
  // Apple Pay, Google Pay and Shop Pay are not blocked by a synthetic replay.
  // Attribution is already synchronised on page load and cart updates; this
  // pointerdown is a final non-blocking best effort before the wallet opens.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.target.closest?.('.shopify-payment-button__button')) return;
      syncCartAttributes({ force: true });
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (checkoutBypass.has(form) || !isCheckoutControl(submitter)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      waitForCheckoutSync().finally(() => {
        checkoutBypass.add(form);
        if (submitter) checkoutBypass.add(submitter);
        if (typeof form.requestSubmit === 'function' && submitter) form.requestSubmit(submitter);
        else form.submit();
        window.setTimeout(() => {
          checkoutBypass.delete(form);
          if (submitter) checkoutBypass.delete(submitter);
        });
      });
    },
    true
  );

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = (state, title, url) => originalPushState(state, title, withAttribution(url));
  window.history.replaceState = (state, title, url) => originalReplaceState(state, title, withAttribution(url));

  captureAttribution();
  syncCartAttributes();

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => syncCartAttributes({ force: true }));
  }

  window.AristaAttribution = Object.freeze({ captureAttribution, syncCartAttributes, withAttribution });
})();
